import { Checkbox } from '@headlessui/react'
import cx from 'classnames'
import { type ComponentProps, useRef } from 'react'

import { useTranslate } from '../../hooks/useTranslate'
import { focusRingStyles } from '../../libs/focusRingStyles'
import { paperStyles } from '../../libs/paperStyles'
import {
  type PropsWithSafeExtra,
  safeRestProps,
} from '../../libs/safeRestProps'
import { Icon } from '../../structure/icon/Icon'
import { inputTypographyStyles } from '../textInput/rawTextInputStyles'

export type RawCheckboxProps = PropsWithSafeExtra<
  'span',
  'className' | 'defaultChecked' | 'id' | 'onClick'
> & {
  editable?: boolean
  isInError?: boolean
} & Pick<
    ComponentProps<typeof Checkbox>,
    'indeterminate' | 'onChange' | 'checked' | 'disabled'
  > & {
    onBlur?: (event: React.FocusEvent<HTMLSpanElement>) => void // when deriving from the Checkbox component, onBlur has 'any' type, don't know why
  }

export function RawCheckbox({
  checked,
  className,
  disabled = false,
  editable = true,
  indeterminate,
  isInError = false,
  extraProps = {},
  ...restProps // Needed since we can pass other props like aria-*, data-* etc. or spread that isn't type checked
}: RawCheckboxProps) {
  const translate = useTranslate()

  /*
   * This ref is for fixing LIFE-8222 where an item is selected in the command palette
   * using the enter key on the keyboard. This trigger onKeyDown, then the modal opens with
   * a checkbox focused ,and the keyup event from the enter key is triggered on the checkbox
   * which submits the form immediately. This state if for swallowing the keyup event unless
   * the keydown was also done with focus on this checkbox.
   */
  const isEnterPressed = useRef(false)

  const state =
    `${isInError ? 'isError' : 'noError'}${checked ? 'Checked' : 'NotChecked'}` as const

  const { onKeyUp, onKeyDown, ...safeProps } = safeRestProps(
    extraProps,
    restProps,
  )

  return editable ? (
    <Checkbox
      {...safeProps}
      onKeyUp={(event) => {
        if (event.key === 'Enter' && !isEnterPressed.current) {
          event.preventDefault() // Prevent default headless onKeyUp behavior that submits a form in a modal that is opened using keyboard enter key. If the first input is a checkbox and it is autofocused.
        }
        isEnterPressed.current = false
        onKeyUp?.(event)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          isEnterPressed.current = true
        }
        onKeyDown?.(event)
      }}
      data-autofocus
      checked={checked}
      aria-invalid={isInError}
      disabled={disabled}
      indeterminate={indeterminate}
      className={cx(
        'rounded-sm flex size-4 transition',
        focusRingStyles({ hasError: isInError }),
        inputTypographyStyles({ isDisabled: disabled }),
        disabled
          ? 'cursor-not-allowed text-amaze-ink bg-slate-200 dark:bg-slate-700'
          : cx(
              'cursor-pointer text-white',
              {
                noErrorNotChecked: cx(
                  paperStyles({ ringColor: 'greyContrast' }),
                  'hover:shadow-xs hover:inset-ring-slate-400 dark:hover:inset-ring-slate-500',
                ),
                noErrorChecked: 'bg-primary-500',
                isErrorNotChecked: paperStyles({ ringColor: 'error' }),
                isErrorChecked: 'bg-error',
              }[state],
            ),
        className,
      )}
    >
      <Icon
        name={indeterminate ? 'Minus' : 'Check'}
        color="unset"
        className={cx('transition-opacity', !checked && 'opacity-0!')}
      />
    </Checkbox>
  ) : (
    <div className="w-full p-2">
      {checked ? translate('boolean.true') : translate('boolean.false')}
    </div>
  )
}
